import React, {useMemo, useState} from "react";
import {Col, Row} from "reactstrap";
import Divider from "@mui/material/Divider";
import Form from "../../base/form";
import {OutlinedInputForm} from "../../base/input/outlined_input";
import Button from "../../base/button/button";
import Dialog from "../../base/dialog";
import * as Yup from "yup";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import {makeRequired, makeValidate} from "mui-rff";
import useIsMounted from "../../../hooks/use-is-mounted";
import Api from "../../../../core/services/api/api";
import {HomepagePlatforms} from "../../../../core/constants/enums";
import {ReactComponent as WebIcon} from '../../../../assets/images/platform-icons/web.svg';
import {ReactComponent as PhoneIcon} from '../../../../assets/images/platform-icons/phone.svg';
import classnames from "classnames";
import {useOperatorInformation} from "../../../hooks/use-operator-information";
import useAccessLevels from "../../../hooks/use-access-levels";

const allPlatforms = [
    {
        id: HomepagePlatforms.web,
        title: "Web",
        icon: <WebIcon/>,
    },
    {
        id: HomepagePlatforms.phone,
        title: "Phone",
        icon: <PhoneIcon/>,
    }
];


const AddNewHomepageEditionDialog = ({open, close, onSuccess}) => {
    const [submitting, setSubmitting] = useState(false);
    const isMounted = useIsMounted();
    const operatorInformation = useOperatorInformation();
    const {packages} = useAccessLevels();
    const canManageWeb = packages.clientPlatform.manageWebHomepageEditions;
    const canManagePhone = packages.clientPlatform.managePhoneHomepageEditions;
    const showPlatformPicker = canManageWeb && canManagePhone;
    const defaultPlatform = useMemo(() => {
        if (canManageWeb && !canManagePhone) {
            return HomepagePlatforms.web;
        }
        if (canManagePhone && !canManageWeb) {
            return HomepagePlatforms.phone;
        }
        return null;
    }, [canManagePhone, canManageWeb]);

    const schema = useMemo(() => Yup.object().shape({
        title: Yup.string().trim().required(ValidateMessages.required),
        platform: Yup.number().nullable().required(ValidateMessages.required).typeError(ValidateMessages.incorrectType("Platform")),
    }), []);
    const validate = useMemo(() => makeValidate(schema), [schema]);
    const required = useMemo(() => makeRequired(schema), [schema]);

    const initialValues = useMemo(() => ({
        title: '',
        platform: defaultPlatform,
    }), [defaultPlatform]);

    /**
     * Creates a new main page edition:
     * - Calls the api regarding this action
     * - if the api call was successful, sets the [editions] in the state so that the added edition is added as the first item after live ones
     */
    const createNewMainPage = async (values) => {
        setSubmitting(true);
        const forApi = {
            name: values.title,
            platformId: values.platform,
        }
        const response = await Api.createHomepageEdition(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            onSuccess({
                ...response.data,
                operatorName: operatorInformation?.name ?? "",
            });
            close(false);
        }
        setSubmitting(false);
    }

    return (
        <>
            <Dialog
                open={open}
                setOpen={close}
                maxWidth={'sm'}
                className={'add-new-main-page-edition-dialog'}
            >
                <Row className='no-gutters justify-content-center align-items-start w-100'>
                    <Row xs={12} className='no-gutters justify-content-center m-3 w-100'>
                        <p className={`text-lg text-color-1 my-2 font-weight-semi-bold text-center`}>
                            Add a New Homepage Edition
                        </p>
                    </Row>
                    <Divider className='divider'/>
                    <div className='my-3 w-100'/>
                    <Form
                        className='w-100 px-4'
                        id={'create-edition-form'}
                        onSubmit={createNewMainPage}
                        validate={validate}
                        initialValues={initialValues}
                        render={({values, form}) => {
                            return (
                                <div className='d-flex flex-wrap justify-content-around w-100'>
                                    <Col xs={12} className='mb-4'>
                                        <OutlinedInputForm
                                            label={'Title'}
                                            type={'text'}
                                            name={`title`}
                                            required={required.title}
                                            value={values.title ?? ''}
                                            onChange={(e) => form.change(e.target.name, e.target.value)}
                                        />
                                    </Col>
                                    {
                                        showPlatformPicker &&
                                        <div className='d-flex flex-wrap w-100 mb-4 justify-content-center'>
                                            {
                                                allPlatforms.map(platform => (
                                                    <div
                                                        key={platform.id}
                                                        className={classnames('platform-selection-card', {'selected': platform.id === values?.platform})}
                                                        onClick={() => !submitting && form.change('platform', platform.id)}
                                                    >
                                                        {platform.icon}
                                                        <p>
                                                            {platform.title}
                                                        </p>
                                                    </div>
                                                ))
                                            }
                                        </div>
                                    }
                                </div>
                            );
                        }}
                    />
                    <div className='my-2 w-100'/>
                    <Divider className='divider'/>
                    <Row className='no-gutters justify-content-between align-items-start w-100'>
                        <Row className='no-gutters justify-content-between align-items-start w-100'>
                            <Button
                                disabled={!open || submitting}
                                variant={'dialog'}
                                color={'primary'}
                                onClick={() => close()}
                                borderRadius={'0 8px 0 8px'}
                            >
                                {submitting ? 'submitting...' : 'Close'}
                            </Button>
                            <Button
                                disabled={!open || submitting}
                                variant={'dialog'}
                                color={'primary'}
                                type={'submit'}
                                form={'create-edition-form'}
                                borderRadius={'8px 0 8px 0'}
                            >
                                {submitting ? 'submitting...' : 'Add'}
                            </Button>
                        </Row>
                    </Row>
                </Row>
            </Dialog>
        </>
    );
}

export default AddNewHomepageEditionDialog;
